import React from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { formatMonthWithYear } from '../../utils/formatters';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { ChartInfoButton } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useEvaluationTrendData } from '../../hooks/useEvaluationQueries';

interface PerformanceTrendAnalysisProps {
  branchId?: number;
  branchName?: string;
  brandName?: string;
  timeRange?: number;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  selectedMetric: string;
  showBrandLine?: boolean;
  showAllRestaurantsLine?: boolean;
  show12MonthsAverageLine?: boolean;
  metrics: Array<{ id: string; name: string; unit: string; type: string }>;
  average12MonthData?: Record<string, number>;
}

const PerformanceTrendAnalysis: React.FC<PerformanceTrendAnalysisProps> = ({
  branchId = 0,
  branchName = "شعبه",
  brandName = "برند",
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  selectedMetric,
  showBrandLine = false,
  showAllRestaurantsLine = false,
  show12MonthsAverageLine = false,
  metrics,
  average12MonthData
}) => {
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();

  // Use React Query hook for data fetching
  const { 
    data,
    isLoading, 
    isError, 
    error 
  } = useEvaluationTrendData(branchId, selectedMetric, timeRange);
  
  const trendData = data?.trendData || [];
  const brandTrendData = data?.brandTrendData || [];
  const allRestaurantsTrendData = data?.allRestaurantsTrendData || [];
  
  // Calculate 12 months average for the branch
  const calculate12MonthsAverage = () => {
    if (trendData.length === 0) return [];
    
    // Use the API data if available, otherwise calculate on frontend as fallback
    const average = average12MonthData && average12MonthData[selectedMetric] !== undefined
      ? average12MonthData[selectedMetric]
      : (() => {
          const last12Months = trendData.slice(-12);
          return last12Months.reduce((sum, item) => sum + item.value, 0) / last12Months.length;
        })();
    
    // Return array with same length as trendData, all with the average value
    return trendData.map(item => ({
      month: item.month,
      value: average
    }));
  };
  
  const average12MonthsData = calculate12MonthsAverage();

  React.useEffect(() => {
    if (isError && onError && error) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  const getSelectedMetricName = () => {
    return metrics.find(m => m.id === selectedMetric)?.name || '';
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-text-secondary">در حال بارگذاری نمودار روند…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-status-error">بارگذاری داده‌ها ناموفق بود؛ لطفاً دوباره تلاش کنید.</div>
        </div>
      </div>
    );
  }

  return (
    <div id="trend-analysis" className="card-base card-padding animate-enter animate-delay-3">
      <ChartInfoButton 
        onClick={() => openGuide(CHART_GUIDES.evaluationScoresTrend)}
        className="mb-4"
      />
      <div className="border-b border-default pb-4 mb-6">
        <h3 className="title-card">
          تحلیل روند عملکرد
        </h3>
        <p className="content-secondary text-sm mt-2 text-center">
          روند {getSelectedMetricName()} در طول دوره زمانی و مقایسه با استانداردها
        </p>
      </div>
      <div className="card-base bg-bg-secondary rounded-xl p-4 shadow-inner border border-border-primary">
        <div className={`${compact ? 'h-60' : 'h-80'} w-full relative`} role="img" aria-label="نمودار تحلیل روند عملکرد">
          <Line
            data={{
              labels: trendData.map((item) => formatMonthWithYear(item.month)),
              datasets: [
                {
                  label: branchName,
                  data: trendData.map((item) => item.value),
                  borderColor: getChartColors(currentTheme)[0],
                  backgroundColor: getChartBackgroundColor(0, currentTheme, 0.25),
                  borderWidth: 4,
                  pointRadius: 4,
                  pointHoverRadius: 12,
                  pointBackgroundColor: getChartColors(currentTheme)[0],
                  pointBorderColor: 'var(--surface-elevated)',
                  pointBorderWidth: 3,
                  pointHoverBorderWidth: 4,
                  tension: 0.4,
                  fill: true,
                },
                ...(showBrandLine ? [{
                  label: `میانگین گروه ${brandName}`,
                  data: brandTrendData.map((item) => item.value),
                  borderColor: getChartColors(currentTheme)[1],
                  backgroundColor: getChartBackgroundColor(1, currentTheme, 0.15),
                  borderWidth: 3,
                  pointRadius: 4,
                  pointHoverRadius: 10,
                  pointBackgroundColor: getChartColors(currentTheme)[1],
                  pointBorderColor: 'var(--surface-elevated)',
                  pointBorderWidth: 2,
                  pointHoverBorderWidth: 3,
                  tension: 0.4,
                  borderDash: [8, 6],
                  fill: false,
                }] : []),
                ...(showAllRestaurantsLine ? [{
                  label: 'میانگین بازار',
                  data: allRestaurantsTrendData.map((item) => item.value),
                  borderColor: getChartColors(currentTheme)[2],
                  backgroundColor: getChartBackgroundColor(2, currentTheme, 0.15),
                  borderWidth: 3,
                  pointRadius: 4,
                  // pointHoverRadius: 6,
                  pointBackgroundColor: getChartColors(currentTheme)[0],
                  pointHoverRadius: 10,
                  pointBorderColor: 'var(--surface-elevated)',
                  pointBorderWidth: 2,
                  pointHoverBorderWidth: 3,
                  tension: 0.4,
                  borderDash: [12, 8],
                  fill: false,
                }] : []),
                ...(show12MonthsAverageLine ? [{
                  label: 'میانگین ۱۲ ماه گذشته',
                  data: average12MonthsData.map((item) => item.value),
                  borderColor: getChartColors(currentTheme)[3] || '#9333EA', // Purple color for 12 months average
                  backgroundColor: getChartBackgroundColor(3, currentTheme, 0.15),
                  borderWidth: 3,
                  pointRadius: 0, // No points for average line
                  pointHoverRadius: 0,
                  tension: 0,
                  borderDash: [5, 5],
                  fill: false,
                }] : []),
              ],
            }}
            options={{
              ...getChartJsOptions(currentTheme),
              plugins: {
                            ...getChartJsOptions(currentTheme).plugins,
                            tooltip: {
                              ...getChartJsOptions(currentTheme).plugins?.tooltip,
                              callbacks: {
                                ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                                label: function(context: any) {
                                  const label = context.dataset.label || '';
                                  const value = context.parsed.y;
                                  const separator = '\u00A0:\u00A0';
                                  const formattedValue = `${value.toFixed ? value.toFixed(1) : value}`;
                                  return `${label}${separator}${formattedValue}`;
                                }
                              }
                            }
                          },
              scales:{
                ...getChartJsOptions(currentTheme).scales,
                x:{
                  ...getChartJsOptions(currentTheme).scales.x,
                  ticks: {
                  ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                  autoSkip: false,
                  maxRotation: 45,
                  minRotation: 45,
                  
                },
                
                },
                y: {
                                  ...getChartJsOptions(currentTheme).scales.y,
                                  ticks: {
                                    ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                                    callback: function (value: any) {
                                      return `${value}`;
                                    }
                                  }
                
                                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrendAnalysis;